import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEmpty, getToken, getRefreshToken } from '../../../utils/helpers';
import DiagnosticIndicator, { DiagnosticStatus, mapStatus } from '../../../components/UI/DiagnosticIndicator';
import { API_ROOT } from '../../../utils/api-config';
import { showModal } from '../../../actions/modal.actions';

const NO_VALUE_STRINGS = ['no value', 'no_value', 'no-value'];
const isNoValue = (val) => typeof val === 'string' && NO_VALUE_STRINGS.includes(val.trim().toLowerCase());

class DeviceDiagnostic extends Component {

    state = {
        diagnostics: {},
        loadingDiagnostics: {},
        deviceStatuses: {},
        deviceLogs: {},
    }

    componentDidMount() {
        this.initDevices();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.serviceorderInfo !== this.props.serviceorderInfo) {
            this.initDevices();
        }
    }

    getDevices = () => {
        const { serviceorderInfo } = this.props;
        if (!serviceorderInfo || !serviceorderInfo.serviceLines) return [];
        return serviceorderInfo.serviceLines.flatMap(sl => sl.devices || []);
    }

    initDevices = () => {
        this.getDevices().forEach(device => {
            if (device.deviceProfileId) {
                this.fetchDiagnostics(device.deviceProfileId, device);
            }
        });
    }

    fetchDiagnostics = async (deviceProfileId, device) => {
        this.setState(prevState => ({
            loadingDiagnostics: { ...prevState.loadingDiagnostics, [deviceProfileId]: true }
        }));

        try {
            const response = await fetch(`${API_ROOT}/provisioning/diagnostics?deviceProfileId=${deviceProfileId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Token': getToken(),
                    'X-API-Refresh-Token': getRefreshToken()
                }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const diagnosticData = await response.json();

            this.setState(prevState => ({
                diagnostics: { ...prevState.diagnostics, [deviceProfileId]: diagnosticData },
                loadingDiagnostics: { ...prevState.loadingDiagnostics, [deviceProfileId]: false }
            }));

            this.autoRunDeviceStatus(deviceProfileId, diagnosticData, device);

        } catch {
            this.setState(prevState => ({
                loadingDiagnostics: { ...prevState.loadingDiagnostics, [deviceProfileId]: false }
            }));
        }
    }

    autoRunDeviceStatus = async (deviceProfileId, diagnosticData, device) => {
        const deviceStatusOperation = diagnosticData?.find(diag => diag.operationName === 'device-status');
        if (!deviceStatusOperation || !device) return;
        await this.runDiagnosticAction('device-status', device, deviceStatusOperation);
    }

    buildDeviceNums = (device) => {
        if (device.apiServiceDeviceNums) {
            return device.apiServiceDeviceNums.filter(num => !isNoValue(num.number));
        }
        if (!device.deviceProperties) return [];
        return device.deviceProperties
            .filter(prop => prop.values && prop.values[0] && prop.values[0][0] && !isNoValue(prop.values[0][0]))
            .map(prop => ({ number: prop.values[0][0], typeName: prop.name }));
    }

    getDisplayProperties = (device) => {
        if (device.apiServiceDeviceNums && device.apiServiceDeviceNums.length > 0) {
            return device.apiServiceDeviceNums.map(num => ({
                key: num.typeName,
                description: num.type || num.typeName,
                value: num.number || '-'
            }));
        }
        if (!device.deviceProperties) return [];
        return device.deviceProperties.map(prop => ({
            key: prop.name,
            description: prop.description || prop.name,
            value: prop.values && prop.values[0] && prop.values[0][0] ? prop.values[0][0] : '-'
        }));
    }

    runDiagnosticAction = async (actionKey, device, diagnosticResponse) => {
        const deviceId = device.id;

        this.setState(prevState => ({
            deviceStatuses: { ...prevState.deviceStatuses, [deviceId]: DiagnosticStatus.LOADING }
        }));

        try {
            const payload = {
                deviceNums: this.buildDeviceNums(device),
                deviceProfile: diagnosticResponse?.deviceProfileName || 'ONT',
                interfaces: [diagnosticResponse?.provisioningInterfaceName || 'CALIX-SMX']
            };

            const response = await fetch(`${API_ROOT}/provisioning/diagnostic/${actionKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Token': getToken(),
                    'X-API-Refresh-Token': getRefreshToken()
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const actionResponseData = await response.json();

            if (actionResponseData && actionResponseData.state) {
                const newStatus = mapStatus(actionResponseData);
                let parsedLogs = actionResponseData.rawResponse;
                if (typeof parsedLogs === 'string') {
                    try {
                        const jsonString = parsedLogs.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false');
                        parsedLogs = JSON.parse(jsonString);
                    } catch {
                        parsedLogs = { error: 'Failed to parse response', raw: parsedLogs };
                    }
                }
                this.setState(prevState => ({
                    deviceStatuses: { ...prevState.deviceStatuses, [deviceId]: newStatus },
                    deviceLogs: { ...prevState.deviceLogs, [deviceId]: parsedLogs }
                }));
            } else {
                this.setState(prevState => ({
                    deviceStatuses: { ...prevState.deviceStatuses, [deviceId]: DiagnosticStatus.UNKNOWN }
                }));
            }
        } catch {
            this.setState(prevState => ({
                deviceStatuses: { ...prevState.deviceStatuses, [deviceId]: DiagnosticStatus.UNKNOWN }
            }));
        }
    }

    showLogsModal = (device, deviceId) => {
        const { diagnostics, deviceLogs, deviceStatuses } = this.state;
        const diagnosticResponse = diagnostics[device.deviceProfileId];
        const deviceStatusDiagnostic = diagnosticResponse?.find(diag => diag.operationName === 'device-status');

        this.props.showModal('EQUIPMENT_DEVICE_STATUS_LOGS_MODAL', {
            device,
            logs: deviceLogs[deviceId],
            status: deviceStatuses[deviceId],
            diagnosticResponse: deviceStatusDiagnostic,
            diagnostics: diagnosticResponse,
            deviceId,
            onUpdateParent: (updatedStatus, updatedLogs) => {
                this.setState(prevState => ({
                    deviceStatuses: { ...prevState.deviceStatuses, [deviceId]: updatedStatus },
                    deviceLogs: { ...prevState.deviceLogs, [deviceId]: updatedLogs }
                }));
            }
        });
    }

    render() {
        const { diagnostics, loadingDiagnostics, deviceStatuses, deviceLogs } = this.state;
        const devices = this.getDevices();

        if (isEmpty(devices)) return null;

        return (
            <div className="plan-section">
                <h5>Equipment</h5>
                {devices.map((device, index) => {
                    const deviceId = device.id;
                    const diagnosticResponse = diagnostics[device.deviceProfileId];
                    const isLoading = loadingDiagnostics[device.deviceProfileId];
                    const hasDeviceDiagnostics = diagnosticResponse && Array.isArray(diagnosticResponse) && diagnosticResponse.length > 0;
                    const deviceStatusDiagnostic = hasDeviceDiagnostics
                        ? diagnosticResponse.find(diag => diag.operationName === 'device-status')
                        : null;
                    const status = deviceStatuses[deviceId] || DiagnosticStatus.UNKNOWN;
                    const logs = deviceLogs[deviceId];

                    return (
                        <div className="package-option" key={index}>
                            <span className="po-title">{device.itemDescription || device.itemName}</span>
                            <div className="container-list-bubble container-list-bubble-equipment" style={{ position: 'relative' }}>
                                {hasDeviceDiagnostics && (
                                    <DiagnosticIndicator
                                        diagnosticResponse={diagnosticResponse}
                                        statusDiagnostic={deviceStatusDiagnostic}
                                        isLoading={isLoading}
                                        onRunAction={this.runDiagnosticAction}
                                        onToggleLogs={() => this.showLogsModal(device, deviceId)}
                                        device={device}
                                        status={status}
                                        logs={logs}
                                        hasLogs={!!logs}
                                        style={{ position: 'absolute', top: '8px', right: '8px' }}
                                    />
                                )}
                                {this.getDisplayProperties(device).map((prop, i) => (
                                    <dl key={i}>
                                        <dt>{prop.description}</dt>
                                        <dd>{prop.value}</dd>
                                    </dl>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
}

const mapDispatchToProps = {
    showModal,
};

export default connect(null, mapDispatchToProps)(DeviceDiagnostic);
